import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CROPS = ["Gehun (Wheat)", "Sugarcane (Ganna)", "Moongphali (Groundnut)", "Cotton (Kapas)", "Vegetables (Sabziyan)", "Orchard (Bagicha/Fal)", "Makka (Maize)", "Sarson (Mustard)"];
const WATER = ["Borewell", "Nahar/Canal", "Kuan (Well)", "Talaab (Pond)"];
const SOIL = ["Normal", "Balu (Sandy)", "Kali Mitti (Black)", "Pathrili (Rocky)"];

export default function CropAdvisorPage() {
  const [crop, setCrop] = useState(CROPS[0]);
  const [land, setLand] = useState("5");
  const [unit, setUnit] = useState("bigha");
  const [water, setWater] = useState(WATER[0]);
  const [soil, setSoil] = useState(SOIL[0]);
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    if (busy) return;
    setBusy(true);
    setResult("");
    try {
      const resp = await fetch(`${API}/ai/crop-advisor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ crop, land_size: parseFloat(land) || 1, land_unit: unit, water_source: water, soil }),
      });
      if (resp.status === 429) {
        setResult("Aaj ki advisor limit poori ho gayi. Kal dobara try karein ya seedha call karein: +91 98290 57721");
        setBusy(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          try {
            const data = JSON.parse(part.slice(5).trim());
            if (data.t) setResult((r) => r + data.t);
            if (data.error) setResult("Maaf kijiye, abhi suggestion nahi ban paya. Dobara try karein.");
          } catch { /* partial */ }
        }
      }
    } catch {
      setResult("Connection issue ho gaya. Dobara try karein.");
    } finally {
      setBusy(false);
    }
  };

  const selectCls =
    "w-full border border-line bg-moss px-4 py-3 text-sm text-cream outline-none transition-colors duration-300 focus:border-harvest";

  return (
    <section data-testid="crop-advisor-page" className="mx-auto max-w-[1440px] px-6 pb-28 pt-32 lg:px-12 lg:pt-40">
      <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }} className="text-xs uppercase tracking-[0.3em] text-harvest">
        AI Crop Advisor
      </motion.p>
      <motion.h1 initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease }} className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
        Apni fasal batao, <span className="italic text-harvest">poora setup pao.</span>
      </motion.h1>
      <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease }} className="mt-6 max-w-2xl text-sm leading-relaxed text-sage sm:text-base">
        Crop, zameen aur paani ki jaankari daaliye — Jamna AI aapko sahi system, models aur layout suggest karega. Hinglish mein, seedha kaam ki baat.
      </motion.p>

      <div className="mt-14 grid gap-10 lg:grid-cols-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease }} className="space-y-5 lg:col-span-5">
          <div>
            <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-sage">Fasal / Crop</label>
            <select value={crop} onChange={(e) => setCrop(e.target.value)} data-testid="advisor-crop" className={selectCls}>
              {CROPS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-sage">Zameen</label>
              <input value={land} onChange={(e) => setLand(e.target.value)} type="number" min="0.5" step="0.5" data-testid="advisor-land" className={selectCls} />
            </div>
            <div>
              <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-sage">Unit</label>
              <select value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="advisor-unit" className={selectCls}>
                <option value="bigha">Bigha</option>
                <option value="acre">Acre</option>
              </select>
            </div>
          </div>
          <div>
            <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-sage">Paani ka source</label>
            <select value={water} onChange={(e) => setWater(e.target.value)} data-testid="advisor-water" className={selectCls}>
              {WATER.map((w) => <option key={w}>{w}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-sage">Mitti</label>
            <select value={soil} onChange={(e) => setSoil(e.target.value)} data-testid="advisor-soil" className={selectCls}>
              {SOIL.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <button
            onClick={ask}
            disabled={busy}
            data-testid="advisor-submit"
            className="group inline-flex items-center gap-3 bg-harvest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-harvestHover disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" />
            {busy ? "Soch raha hoon…" : "Setup Suggest Karo"}
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25, ease }} className="lg:col-span-7">
          <div data-testid="advisor-result" className="min-h-[320px] whitespace-pre-wrap border border-line bg-moss p-8 text-sm leading-relaxed text-cream/90">
            {result || (busy ? "AI aapke liye best setup soch raha hai…" : "Result yahan dikhega — crop chun kar 'Setup Suggest Karo' dabaiye.")}
          </div>
          {result && !busy && (
            <Link to="/contact" data-testid="advisor-enquire-link" className="group mt-6 inline-flex items-center gap-3 border border-harvest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-harvest transition-colors duration-300 hover:bg-harvest hover:text-forest">
              Is setup ke liye enquiry karein
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          )}
        </motion.div>
      </div>
    </section>
  );
}
